import React, { Component } from 'react';
import {
  string, func, shape, object,
} from 'prop-types';
import { ThemeProvider, createGlobalStyle } from 'styled-components';

import theme from './styles-and-themes/themes/theme';
import AppContext from './AppContext';

import SecurityQuestionsPage from './pages/SecurityQuestionsPage/SecurityQuestionsPage';
import RegisterPage from './pages/RegisterPage/RegisterPage';
import LoginPage from './pages/LoginPage/LoginPage';
import OIDCLogoutPage from './pages/OIDCLogoutPage/OIDCLogoutPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage/ResetPasswordPage';
import ConfirmationCompletePage from './pages/ConfirmationCompletePage/ConfirmationCompletePage';
import AcceptEmailConfirmationPage from './pages/AcceptEmailConfirmationPage/AcceptEmailConfirmationPage'; // TODO: Revise and Change workflow (Current = API, then UI) (New = UI, then API call, then confirmation)
import ProfilePage from './pages/ProfilePage/ProfilePage';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';
import RegistrationEntryPage from './pages/RegistrationEntryPage/RegistrationEntryPage';

import PortalLanding from './pages/portal/Landing';
import CreateAccount from './pages/portal/CreateAccount';
import PortalUsers from './pages/portal/Users';

import { makeLowerCaseKeys } from '../shared-with-server/route-utils';
import { validateAdminCookie } from './utils/jwt-utils';
import {
  instanaKey, arroyoUrl, frontEndUrl, isValidRedirectInfo,
} from './destructured-front-end-config';
import { combineHostAndPath } from '../shared-with-server/destination-utils';
import RegistrationStep2Page from './pages/RegistrationStep2Page/RegistrationStep2Page';
import LoginUAMPage from './pages/LoginUAMPage/LoginUAMPage';
import RegistrationStep1Page from './pages/RegistrationStep1Page/RegistrationStep1Page';
import RegistrationEmailPage from './pages/RegistrationEmailPage/RegistrationEmailPage';
import InstructorVerificationPage from './pages/InstructorVerificationPage/InstructorVerificationPage';

// eslint-ignore no-unused-expressions
const GlobalStyle = createGlobalStyle`
  html, body {
    height: 100%;
    background-color: ${(p) => p.theme.defaultBackgroundColor};
    color: ${(p) => p.theme.defaultForegroundColor};
    margin: 0;
  }
  body {
    margin: 0;
    padding: 0;
    font-family: 'Source Sans Pro', sans-serif;
  }
  * {
    box-sizing: border-box;
  }
  ul, li {
    -webkit-margin-before: 0;
    -webkit-margin-after: 0;
    -webkit-margin-start: 0px;
    -webkit-margin-end: 0px;
    -webkit-padding-start: 0;
  }
  .logo {
    max-width: 440px;
  }
  #app {
    display: flex;
    min-height: 100%;
  }
`;

const Pages = {
  '/': LoginPage,
  '/login': LoginPage,
  '/catalog/login': LoginPage,
  '/oidc/session/end': OIDCLogoutPage,
  '/oidc/session/end/success': OIDCLogoutPage,
  '/catalog/register': RegisterPage,
  '/register': RegisterPage,
  '/ContinueRegistration': RegistrationEntryPage,
  '/uam/login': LoginUAMPage,
  '/uam/email': RegistrationEmailPage,
  '/uam/step1': RegistrationStep1Page,
  '/uam/step2': RegistrationStep2Page,
  '/uam/instructor-verification': InstructorVerificationPage,
  '/confirm': ConfirmationCompletePage,
  '/AcceptEmail': AcceptEmailConfirmationPage,
  '/profile': ProfilePage,
  '/security-questions': SecurityQuestionsPage,
  '/error': ErrorPage,
  '/Validate': ResetPasswordPage,
  '/ResetPassword': ResetPasswordPage,
  '/ForgotPassword': ForgotPasswordPage,
  '/portal': PortalLanding,
  '/portal/CreateAccount': CreateAccount,
  '/portal/users': PortalUsers,
};

const lcPages = makeLowerCaseKeys(Pages);

class App extends Component {
  constructor(props) {
    super(props);
    const userRoles = validateAdminCookie();
    /* eslint-disable react/no-unused-state */
    this.state = {
      userRoles,
    };
  }

  // Loads Instana agent on front-end
  componentDidMount() {
    if (instanaKey && instanaKey.length) {
      // Set config for external loader script (CSP-compliant approach)
      window.__IAM_INSTANA_CONFIG__ = {
        key: instanaKey,
        reportingUrl: 'https://eum-red-saas.instana.io',
      };

      // Load external Instana loader script
      const instanaLoader = document.createElement('script');
      instanaLoader.type = 'text/javascript';
      instanaLoader.async = true;
      instanaLoader.src = combineHostAndPath(frontEndUrl, '/static/instana-loader.js');
      document.head.appendChild(instanaLoader);
    }

    if (arroyoUrl?.length) {
      // Set config for external loader script (CSP-compliant approach)
      window.__IAM_ARROYO_CONFIG__ = {
        baseUrl: arroyoUrl,
      };

      // Load external Arroyo loader script
      const arroyoLoader = document.createElement('script');
      arroyoLoader.type = 'text/javascript';
      arroyoLoader.async = true;
      arroyoLoader.src = combineHostAndPath(frontEndUrl, '/static/arroyo-loader.js');
      document.head.appendChild(arroyoLoader);
    }
  }

  // Enhances Instana error handling by providing React call stack
  componentDidCatch(error, info) { // eslint-disable-line class-methods-use-this
    if (instanaKey && instanaKey.length) {
      ineum('reportError', error, { // eslint-disable-line no-undef
        componentStack: info.componentStack,
      });
    }
  }

  render() {
    const oidcSettings = JSON.parse(localStorage.getItem('oidcSettings'));

    const getPage = (trimmedPathname) => {
      const trimmedPathParts = trimmedPathname.split('/');
      if (trimmedPathParts.length < 2 || trimmedPathParts[trimmedPathParts.length - 2].toLowerCase() !== 'interaction') {
        return (Pages[trimmedPathname] || lcPages[trimmedPathname.toLowerCase()]);
      }

      const oidcPrompts = {
        login: LoginPage,
        consent: LoginPage,
      };

      return oidcPrompts[(oidcSettings.prompt?.name || 'none')];
    };

    const { pathname } = this.props;
    const trimmedPathname = (pathname.length > 1 && pathname.endsWith('/') ? pathname.substr(0, pathname.length - 1) : pathname);

    const Page = getPage(trimmedPathname);

    const validPath = Page !== undefined && isValidRedirectInfo;

    return (
      <ThemeProvider theme={theme} className="container">
        <AppContext.Provider value={this.state}>
          {
            validPath
              ? <Page {...this.props} oidcSettings={oidcSettings} />
              : <NotFoundPage title="Page Not Found" error="pageNotFound" {...this.props} />
          }
          <GlobalStyle />
        </AppContext.Provider>
      </ThemeProvider>
    );
  }
}

App.propTypes = {
  pathname: string.isRequired,
  getBrandedResource: func.isRequired,
  queryStringData: object.isRequired,
  destPath: shape({
    dest: string,
    path: string,
    retURL: string,
  }),
  brand: shape({
    short: string.isRequired,
    full: string.isRequired,
  }).isRequired,
};

App.defaultProps = {
  destPath: null,
};

export default App;
